import { Controller } from "@hotwired/stimulus";

/**
 * Registration Form Controller
 * Validates email format and password match on the registration form.
 * Email must have a valid domain with TLD (e.g., name@example.com)
 */

export default class extends Controller<HTMLFormElement> {
  static targets = [
    "username",
    "email",
    "password",
    "passwordConfirmation",
    "submit",
    "usernameError",
    "usernameHint",
    "emailError",
    "passwordError",
    "passwordConfirmationError",
  ];

  declare readonly usernameTarget: HTMLInputElement;
  declare readonly emailTarget: HTMLInputElement;
  declare readonly passwordTarget: HTMLInputElement;
  declare readonly passwordConfirmationTarget: HTMLInputElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly usernameErrorTarget: HTMLElement;
  declare readonly usernameHintTarget: HTMLElement;
  declare readonly emailErrorTarget: HTMLElement;
  declare readonly passwordErrorTarget: HTMLElement;
  declare readonly passwordConfirmationErrorTarget: HTMLElement;
  declare readonly hasUsernameErrorTarget: boolean;
  declare readonly hasUsernameHintTarget: boolean;
  declare readonly hasEmailErrorTarget: boolean;
  declare readonly hasPasswordErrorTarget: boolean;
  declare readonly hasPasswordConfirmationErrorTarget: boolean;

  // Email regex: requires user@domain.tld format
  private readonly emailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Username validation state
  private isUsernameAvailable = false;
  private usernameCheckTimeout: ReturnType<typeof setTimeout> | null = null;

  connect(): void {
    this.validateForm();
    this.element.addEventListener("input", this.handleFormInput);
  }

  disconnect(): void {
    this.element.removeEventListener("input", this.handleFormInput);
  }

  private handleFormInput = (): void => {
    this.validateForm();
  };

  validateUsername(): void {
    const input = this.usernameTarget;
    const value = input.value.trim();

    // Clear previous timeout
    if (this.usernameCheckTimeout) {
      clearTimeout(this.usernameCheckTimeout);
    }

    // Basic validation first
    if (value.length === 0) {
      this.toggleFieldValidation(input, false);
      this.isUsernameAvailable = false;
      this.hideUsernameError();
      this.showUsernameHint();
      this.validateForm();
      return;
    }

    if (value.length < 3) {
      this.showUsernameError("Username must be at least 3 characters");
      this.isUsernameAvailable = false;
      this.validateForm();
      return;
    }

    if (value.length > 20) {
      this.showUsernameError("Username must be at most 20 characters");
      this.isUsernameAvailable = false;
      this.validateForm();
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      this.showUsernameError("Only letters, numbers, and underscores allowed");
      this.isUsernameAvailable = false;
      this.validateForm();
      return;
    }

    // Show checking state
    input.classList.remove("input-success", "input-error");
    this.hideUsernameHint();
    if (this.hasUsernameErrorTarget) {
      this.usernameErrorTarget.textContent = "Checking availability...";
      this.usernameErrorTarget.classList.remove("hidden", "text-error");
      this.usernameErrorTarget.classList.add("text-base-content/50");
    }

    // Debounce the API call
    this.usernameCheckTimeout = setTimeout(() => {
      this.checkUsernameAvailability(value);
    }, 300);
  }

  private async checkUsernameAvailability(username: string): Promise<void> {
    try {
      const response = await fetch(
        `/users/check_username?username=${encodeURIComponent(username)}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();
      const input = this.usernameTarget;

      // Make sure we're still checking the same username
      if (input.value.trim().toLowerCase() !== username.toLowerCase()) {
        return;
      }

      if (data.available) {
        this.isUsernameAvailable = true;
        this.toggleFieldValidation(input, true);
        this.hideUsernameError();
        this.hideUsernameHint();
      } else {
        this.isUsernameAvailable = false;
        this.showUsernameError(data.error || "Username is not available");
      }

      this.validateForm();
    } catch {
      // On network error, allow form submission (server will validate)
      this.isUsernameAvailable = true;
      this.validateForm();
    }
  }

  private showUsernameError(message: string): void {
    const input = this.usernameTarget;
    this.toggleFieldValidation(input, false);
    this.hideUsernameHint();

    if (this.hasUsernameErrorTarget) {
      this.usernameErrorTarget.textContent = message;
      this.usernameErrorTarget.classList.remove("hidden", "text-base-content/50");
      this.usernameErrorTarget.classList.add("text-error");
    }
  }

  private hideUsernameError(): void {
    if (this.hasUsernameErrorTarget) {
      this.usernameErrorTarget.classList.add("hidden");
    }
  }

  private showUsernameHint(): void {
    if (this.hasUsernameHintTarget) {
      this.usernameHintTarget.classList.remove("hidden");
    }
  }

  private hideUsernameHint(): void {
    if (this.hasUsernameHintTarget) {
      this.usernameHintTarget.classList.add("hidden");
    }
  }

  validateEmail(): void {
    const input = this.emailTarget;
    const value = input.value.trim();
    const isValid = this.emailRegex.test(value);

    this.toggleFieldValidation(input, isValid);

    if (this.hasEmailErrorTarget) {
      if (!isValid && value.length > 0) {
        this.emailErrorTarget.textContent =
          "Please enter a valid email address (e.g., name@example.com)";
        this.emailErrorTarget.classList.remove("hidden");
      } else {
        this.emailErrorTarget.classList.add("hidden");
      }
    }

    this.validateForm();
  }

  validatePassword(): void {
    const input = this.passwordTarget;
    const isValid = input.value.length >= 8;

    this.toggleFieldValidation(input, isValid);

    if (this.hasPasswordErrorTarget) {
      if (!isValid && input.value.length > 0) {
        this.passwordErrorTarget.textContent =
          "Password must be at least 8 characters";
        this.passwordErrorTarget.classList.remove("hidden");
      } else {
        this.passwordErrorTarget.classList.add("hidden");
      }
    }

    // Also validate confirmation if it has a value
    if (this.passwordConfirmationTarget.value.length > 0) {
      this.validatePasswordMatch();
    }

    this.validateForm();
  }

  validatePasswordMatch(): void {
    const input = this.passwordConfirmationTarget;
    const isValid =
      input.value === this.passwordTarget.value && input.value.length >= 8;

    this.toggleFieldValidation(input, isValid);

    if (this.hasPasswordConfirmationErrorTarget) {
      if (!isValid && input.value.length > 0) {
        this.passwordConfirmationErrorTarget.textContent =
          "Passwords don't match";
        this.passwordConfirmationErrorTarget.classList.remove("hidden");
      } else {
        this.passwordConfirmationErrorTarget.classList.add("hidden");
      }
    }

    this.validateForm();
  }

  private validateForm(): void {
    requestAnimationFrame(() => {
      const username = this.usernameTarget.value.trim();
      const email = this.emailTarget.value.trim();
      const password = this.passwordTarget.value;
      const passwordConfirmation = this.passwordConfirmationTarget.value;

      const isUsernameFormatValid =
        username.length >= 3 &&
        username.length <= 20 &&
        /^[a-zA-Z0-9_]+$/.test(username);
      const isEmailValid = this.emailRegex.test(email);
      const isPasswordValid = password.length >= 8;
      const isPasswordMatch = password === passwordConfirmation;

      const isFormValid =
        isUsernameFormatValid &&
        this.isUsernameAvailable &&
        isEmailValid &&
        isPasswordValid &&
        isPasswordMatch;

      this.submitTarget.disabled = !isFormValid;
    });
  }

  private toggleFieldValidation(
    input: HTMLInputElement,
    isValid: boolean
  ): void {
    if (input.value.length === 0) {
      input.classList.remove("input-success", "input-error");
      return;
    }

    if (isValid) {
      input.classList.add("input-success");
      input.classList.remove("input-error");
    } else {
      input.classList.add("input-error");
      input.classList.remove("input-success");
    }
  }
}
